import 'rxjs/add/observable/of';
import 'rxjs/add/observable/from';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/bufferWhen';
import 'rxjs/add/operator/exhaustMap';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/filter';
import isEmpty from 'lodash/isEmpty';
import { Observable } from 'rxjs/Observable';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	REFRESH_SOURCE_SPRINT_COMPLETE,
	REFRESH_SOURCE_GLOBAL_ISSUE_CREATE,
	REFRESH_SOURCE_ON_MOUNT,
} from '../../model/constants.tsx';
import { makeServiceContext } from '../../services/service-context/index.tsx';
import {
	checkGlobalIssueCreate,
	loadAppData,
	loadConfigData,
	REFRESH_DATA_REQUEST,
	refreshDataFailure,
	type RefreshDataRequestAction,
} from '../../state/actions/app/index.tsx';
import { closeModal } from '../../state/actions/modals/index.tsx';
import { loadPriorities } from '../../state/actions/priorities/index.tsx';
import { isDragging } from '../../state/selectors/drag/index.tsx';
import { hasAnyActiveOptimistic } from '../../state/selectors/optimistic/index.tsx';
import type { MiddlewareAPI, ActionsObservable, Action } from '../../state/types.tsx';
import { fetchData as fetchBoardScopeData } from '../load-agility-graphql-data/index.tsx';

const hasSprintCompleteSource = (actions: RefreshDataRequestAction[]) =>
	actions.some((action) => action.payload.source === REFRESH_SOURCE_SPRINT_COMPLETE);

export const addCloseModalActionIfSprintCompleted = (
	refreshActions: RefreshDataRequestAction[],
	resultActions: Action[],
): Action[] =>
	hasSprintCompleteSource(refreshActions) ? [...resultActions, closeModal()] : resultActions;

const getGlobalIssueCreateSource = (actions: RefreshDataRequestAction[]) =>
	actions.find((action) => action.payload.source === REFRESH_SOURCE_GLOBAL_ISSUE_CREATE);

export const checkIssuesIfGlobalIssueCreate = (
	refreshActions: RefreshDataRequestAction[],
	resultActions: Action[],
): Action[] => {
	const globalIssueCreateAction = getGlobalIssueCreateSource(refreshActions);
	if (globalIssueCreateAction === undefined) {
		return resultActions;
	}
	const globalIssues = globalIssueCreateAction?.payload?.issues;
	return globalIssues === undefined
		? [...resultActions]
		: [...resultActions, checkGlobalIssueCreate(globalIssues)];
};

const isOptimitsticOrDragging = (store: MiddlewareAPI) => {
	const state = store.getState();
	return hasAnyActiveOptimistic(state) || isDragging(state);
};

const shouldCloseBuffer = (action$: ActionsObservable, store: MiddlewareAPI) =>
	action$.filter(() => !isOptimitsticOrDragging(store));

const shouldAbort = (action$: ActionsObservable, store: MiddlewareAPI) =>
	action$.filter(() => isOptimitsticOrDragging(store));

export const checkIfRefreshOnMount = (actions: RefreshDataRequestAction[]) =>
	actions.some((action) => action.payload.source === REFRESH_SOURCE_ON_MOUNT);

/**
 * Handles data refresh.
 *
 * It buffers incoming actions under the following conditions:
 *
 * - There's an ongoing optimistic transaction;
 * - There's an ongoing drag operation;
 *
 * After the following conditions above are completed the buffered actions are emitted.
 *
 * Actions flushed from the buffer are processed one at the time,
 * and while an action is being processed others will be ignored,
 * therefore guaranteeing that only one request will be handled at the time.
 *
 * While an action is being processed, it may be aborted in case at least one of the
 * following conditions above starts
 */
export const refreshDataEpic = (action$: ActionsObservable, store: MiddlewareAPI) =>
	action$
		.ofType(REFRESH_DATA_REQUEST)
		.bufferWhen(() => shouldCloseBuffer(action$, store))
		.filter((buffer) => !isEmpty(buffer))
		.exhaustMap((refreshActions: RefreshDataRequestAction[]) => {
			const serviceContext = makeServiceContext(store.getState());
			const gicSourceRefreshAction = getGlobalIssueCreateSource(refreshActions);
			if (serviceContext.isCompanyManaged) {
				const loadAppDataAction =
					gicSourceRefreshAction !== undefined
						? loadAppData({
								storeFieldsActions: gicSourceRefreshAction.payload.storeFieldsActions,
							})
						: loadAppData();
				const actions = [loadAppDataAction, loadConfigData()];
				return Observable.from([
					...addCloseModalActionIfSprintCompleted(refreshActions, []),
					...checkIssuesIfGlobalIssueCreate(refreshActions, []),
					...actions,
					...[loadPriorities()],
				]).takeUntil(shouldAbort(action$, store));
			}
			return fetchBoardScopeData({
				store,
				...(gicSourceRefreshAction &&
					gicSourceRefreshAction.payload.storeFieldsActions && {
						storeFieldsActions: gicSourceRefreshAction.payload.storeFieldsActions,
					}),
			})
				.flatMap((resultAction) => [
					...addCloseModalActionIfSprintCompleted(refreshActions, [resultAction]),
					...checkIssuesIfGlobalIssueCreate(refreshActions, [resultAction]),
				])
				.takeUntil(shouldAbort(action$, store));
		})
		.catch((error) => {
			log.safeErrorWithoutCustomerData(
				'backlog.refresh-data.failure',
				'Fail to refresh data.',
				error,
			);
			return Observable.of(refreshDataFailure());
		});
